import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Fade,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Replay as ReplayIcon,
  TouchApp as TouchAppIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  useAutoAnalyzeImage,
  useAutoRegisterLostItem,
  useFileUpload,
  AutoAnalysisSnapshot,
} from '../hooks/useApi';
import { useNotification } from '../contexts/NotificationContext';
import StoreSelectImproved from './StoreSelectImproved';

const commonLocations = [
  'レジ付近',
  '試着室',
  '入口・出口',
  '休憩スペース',
  'トイレ',
  '駐車場',
  '階段',
  'エレベーター',
  'エスカレーター',
  '店舗外',
  'その他',
];

const statusOptions = [
  { value: 'Found', label: '未引き取り' }, 
  { value: 'Claimed', label: '引き取り済み' }, 
  { value: 'Returned', label: '返却完了' }, 
  { value: 'Disposed', label: '処分済み' }, 
];

const LostItemAutoRegisterPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { showError, showSuccess } = useNotification();

  const autoAnalyzeMutation = useAutoAnalyzeImage();
  const autoRegisterMutation = useAutoRegisterLostItem();
  const fileUploadMutation = useFileUpload();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [analysisSnapshot, setAnalysisSnapshot] = useState<AutoAnalysisSnapshot | null>(null);

  const [nameInput, setNameInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  const [foundLocation, setFoundLocation] = useState('');
  const [foundAt, setFoundAt] = useState(() => {
    const now = new Date();
    const jstOffset = 9 * 60; // JST is UTC+9
    const jstTime = new Date(now.getTime() + (jstOffset * 60 * 1000));
    return jstTime.toISOString().slice(0, 16);
  });
  const [storeId, setStoreId] = useState('');
  const [status, setStatus] = useState('Found');
  const [notes, setNotes] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');

  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (userProfile?.store_id) {
      setStoreId(userProfile.store_id);
    }
  }, [userProfile]);

  useEffect(() => () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);



  const resetAnalysisState = useCallback(() => {
    setAnalysisSnapshot(null);
    setNameInput('');
    setDescriptionInput('');
    setCategoryInput('');
    setTagsInput('');
  }, []);

  const runAutoAnalysis = useCallback(async (imageUrl: string, mimeType: string) => {
    try {
      const snapshot = await autoAnalyzeMutation.mutateAsync({
        image_url: imageUrl,
        mime_type: mimeType,
        additional_context: additionalContext.trim() || undefined,
      });
      setAnalysisSnapshot(snapshot);

      const analysis = snapshot.analysis;
      if (analysis.suggested_name) {
        setNameInput(analysis.suggested_name);
      }
      if (analysis.suggested_description) {
        setDescriptionInput(analysis.suggested_description);
      }
      if (analysis.suggested_category) {
        setCategoryInput(analysis.suggested_category);
      }
      if (analysis.suggested_tags?.length) {
        setTagsInput(analysis.suggested_tags.join(', '));
      }

      showSuccess('AI解析が完了しました');
    } catch (error: any) {
      console.error('Auto analysis failed', error);
      showError(error?.message || 'AI解析に失敗しました');
    }
  }, [additionalContext, autoAnalyzeMutation, showError, showSuccess]);

  const handleFileSelect = useCallback(async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      showError('ファイルサイズは10MB以下にしてください');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showError('画像ファイルを選択してください');
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    resetAnalysisState();

    try {
      setIsUploading(true);
      const [imageUrl] = await fileUploadMutation.mutateAsync([file]);
      setUploadedImageUrl(imageUrl);
      await runAutoAnalysis(imageUrl, file.type);
    } catch (error: any) {
      console.error('File upload failed', error);
      showError(error?.message || '画像のアップロードに失敗しました');
    } finally {
      setIsUploading(false);
    }
  }, [fileUploadMutation, previewUrl, resetAnalysisState, runAutoAnalysis, showError]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleAnalyzeAgain = useCallback(async () => {
    if (!uploadedImageUrl || !selectedFile) {
      showError('解析する画像を先にアップロードしてください');
      return;
    }

    await runAutoAnalysis(uploadedImageUrl, selectedFile.type);
  }, [runAutoAnalysis, selectedFile, showError, uploadedImageUrl]);

  const handleRegister = useCallback(async () => {
    if (!currentUser) {
      showError('ログインが必要です');
      return;
    }

    if (!uploadedImageUrl || !analysisSnapshot) {
      showError('画像の解析を完了してください');
      return;
    }

    if (!foundLocation.trim()) {
      showError('発見場所を入力してください');
      return;
    }

    if (!foundAt) {
      showError('発見日時を入力してください');
      return;
    }

    if (!storeId) {
      showError('店舗を選択してください');
      return;
    }

    const tags = tagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);

    const uniqueTags = Array.from(new Map(tags.map(tag => [tag.toLowerCase(), tag])).values());

    try {
      const payload = {
        image_url: uploadedImageUrl,
        mime_type: selectedFile?.type,
        additional_context: additionalContext.trim() || undefined,
        found_location: foundLocation.trim(),
        found_at: new Date(foundAt).toISOString(),
        store_id: storeId,
        status,
        override_name: nameInput.trim() || undefined,
        override_description: descriptionInput.trim() || undefined,
        category: categoryInput.trim() || undefined,
        tags: uniqueTags.length ? uniqueTags : undefined,
        notes: notes.trim() || undefined,
      };

      const response = await autoRegisterMutation.mutateAsync(payload);
      showSuccess('AI解析結果をもとに落とし物を登録しました');
      navigate(`/items/${response.item.id}`);
    } catch (error: any) {
      console.error('Auto registration failed', error);
      showError(error?.message || '自動登録に失敗しました');
    }
  }, [
    additionalContext,
    analysisSnapshot,
    autoRegisterMutation,
    categoryInput,
    currentUser,
    descriptionInput,
    foundLocation,
    foundAt,
    nameInput,
    navigate,
    notes,
    selectedFile,
    showError,
    showSuccess,
    status,
    storeId,
    tagsInput,
    uploadedImageUrl,
  ]);

  const isAnalyzing = autoAnalyzeMutation.isPending;
  const isRegistering = autoRegisterMutation.isPending;

  if (!currentUser) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">この機能を利用するにはログインが必要です。</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            AI自動登録
          </Typography>
          <Typography variant="body1" color="text.secondary">
            画像をアップロードするとAIが解析し、必須項目を除いた情報を自動で提案します。
          </Typography>
        </Box>

        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
              <Box
                component="label"
                sx={{
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  flex: '0 0 320px',
                  cursor: 'pointer',
                  backgroundColor: theme.palette.action.hover,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.text.secondary }} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  画像をアップロード
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  クリックして選択またはドラッグ&ドロップ（最大10MB）
                </Typography>
                {isUploading && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                    <Typography variant="caption" color="text.secondary">
                      アップロード中...
                    </Typography>
                  </Box>
                )}
              </Box>

              {selectedFile && previewUrl && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    プレビュー
                  </Typography>
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      maxHeight: 360,
                      objectFit: 'contain',
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                  <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} alignItems="center">
                    <Button
                      variant="outlined"
                      startIcon={<ReplayIcon />}
                      onClick={handleAnalyzeAgain}
                      disabled={isAnalyzing || isUploading}
                    >
                      再解析
                    </Button>
                    {isAnalyzing && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          AI解析中...
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
          <Box flex={1}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <TouchAppIcon color="primary" />
                  <Typography variant="h6">手入力（必須）</Typography>
                </Stack>
                <Stack spacing={2}>
                  <Autocomplete
                    freeSolo
                    options={commonLocations}
                    value={foundLocation}
                    onChange={(event, newValue) => {
                      setFoundLocation(newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFoundLocation(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="発見場所"
                        required
                        helperText="プリセットから選択、または自由に入力してください"
                        placeholder="例: レジ付近、3階エスカレーター前など"
                      />
                    )}
                  />

                  <TextField
                    label="発見日時"
                    type="datetime-local"
                    value={foundAt}
                    onChange={(event) => setFoundAt(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />

                  <StoreSelectImproved
                    value={storeId}
                    onChange={setStoreId}
                    label="店舗"
                    placeholder="店舗を検索して選択してください"
                    allowManualInput={userProfile?.roles.includes('admin') ?? false}
                    showMap
                    helperText="登録先の店舗を選択してください"
                    disabled={!!userProfile?.store_id && userProfile.roles.includes('staff')}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Box flex={1}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <InfoIcon color="action" />
                  <Typography variant="h6">手入力（任意）</Typography>
                </Stack>
                <Stack spacing={2}>
                  <TextField
                    label="AI解析の補足情報"
                    value={additionalContext}
                    onChange={(event) => setAdditionalContext(event.target.value)}
                    placeholder="ブランド名や状況など、AIに伝えたい情報があれば入力してください"
                    multiline
                    minRows={2}
                  />
                  <FormControl fullWidth>
                    <InputLabel>ステータス</InputLabel>
                    <Select
                      value={status}
                      label="ステータス"
                      onChange={(event) => setStatus(event.target.value)}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="内部メモ（任意）"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    multiline
                    minRows={3}
                    placeholder="管理用の連絡事項等があれば入力してください"
                  />
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Stack>

        <Card variant="outlined">
          <CardContent>
         

            {!analysisSnapshot ? (
              <Alert severity="info">
                画像をアップロードするとAIが名称・説明・カテゴリなどを自動提案します。
              </Alert>
            ) : (
              <Fade in>
                <Stack spacing={2}>
                  <TextField
                    label="アイテム名（AI提案）"
                    value={nameInput}
                    onChange={(event) => setNameInput(event.target.value)}
                    required
                    helperText="必要に応じて修正できます"
                  />
                  <TextField
                    label="説明（AI提案）"
                    value={descriptionInput}
                    onChange={(event) => setDescriptionInput(event.target.value)}
                    multiline
                    minRows={4}
                    helperText="色・形・特徴などが自動入力されます。修正も可能です"
                  />
                  <TextField
                    label="カテゴリ（AI提案）"
                    value={categoryInput}
                    onChange={(event) => setCategoryInput(event.target.value)}
                    helperText="該当しなければ空欄でも構いません"
                  />
                  <TextField
                    label="タグ（カンマ区切り）"
                    value={tagsInput}
                    onChange={(event) => setTagsInput(event.target.value)}
                    helperText="AIが抽出したキーワード。カンマ区切りで編集できます"
                  />
                  {analysisSnapshot.analysis.raw_text && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        AI解析詳細
                      </Typography>
                      <Card variant="outlined" sx={{ backgroundColor: theme.palette.action.hover }}>
                        <CardContent>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {analysisSnapshot.analysis.raw_text}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  )}
                </Stack>
              </Fade>
            )}
          </CardContent>
        </Card>

        <Divider />

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={() => navigate('/items')}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={isRegistering ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
            onClick={handleRegister}
            disabled={isRegistering || isAnalyzing || !uploadedImageUrl}
          >
            {isRegistering ? '登録中...' : 'AI解析結果で登録'}
          </Button>
        </Box>
      </Stack>
    </Container>
  );
};

export default LostItemAutoRegisterPage;
